import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, qs } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, KpiCard, SectionTitle, VarianceIndicator } from "@/components/Kpi";
import { COLORS, ComparisonChart } from "@/components/Charts";
import { usePalette } from "@/lib/palette";
import { dateIt, eurShort, num, statoClass, statoLabel } from "@/lib/format";
import DealerForm from "@/components/DealerForm";
import { ScorporoDealer } from "@/components/ScorporoDealer";
import { PuntiVenditaScheda } from "@/components/PuntiVendita";
import { BottoneScarica } from "@/components/BottoneScarica";
import { RappelProvvigioni } from "@/components/RappelProvvigioni";
import { useFiltersMeta } from "@/components/Filters";

export default function DealerDetail() {
  usePalette();
  const { codice } = useParams();
  const [d, setD] = useState(null);
  const { user, puo } = useAuth();
  const [pratiche, setPratiche] = useState([]);
  const [modifica, setModifica] = useState(false);
  const [reload, setReload] = useState(0);
  const [eliminando, setEliminando] = useState(false);
  const [alData, setAlData] = useState("");
  const meta = useFiltersMeta();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/dealers/${codice}`).then((r) => setD(r.data)).catch(() => setD(false));
    api.get(`/pratiche?${qs({ dealer: codice, page_size: 20 })}`).then((r) => setPratiche(r.data.rows)).catch(() => setPratiche([]));
  }, [codice, reload]);

  const elimina = async () => {
    if (!window.confirm(`Eliminare definitivamente l'anagrafica "${d.dealer.ragione_sociale}" `
      + `(codice ${codice})?\n\nSe il convenzionato ha pratiche collegate l'eliminazione viene `
      + `bloccata: in quel caso imposta la data di chiusura.`)) return;
    setEliminando(true);
    try {
      await api.delete(`/dealers/${codice}`);
      toast.success("Anagrafica eliminata");
      navigate("/convenzionati");
    } catch (e) { toast.error(apiError(e)); }
    setEliminando(false);
  };

  if (d === false) return <Card className="p-8 text-sm text-red-600">Dealer non trovato o accesso non consentito.</Card>;
  if (!d) return <div className="text-sm text-slate-500">Caricamento…</div>;
  const t = d.totali;
  const anno = d.anno;
  const serie = d.serie.serie;
  const p = d.proiezioni;
  const sommaPremi = (r, a) => ["cpi", "cvt", "nfk", "ggn"].reduce((t, k) => t + (r[`${k}_${a}`] || 0), 0) || null;
  const seriePremi = serie.map((r) => ({
    ...r, [`premi_${anno}`]: sommaPremi(r, anno), [`premi_${anno - 1}`]: sommaPremi(r, anno - 1),
  }));

  const togglePdfKpi = async (e) => {
    const mostra = e.target.checked;
    try {
      await api.patch(`/dealers/${codice}/pdf-kpi-servizio`, { mostra });
      setD((x) => ({ ...x, dealer: { ...x.dealer, pdf_mostra_kpi_servizio: mostra } }));
      toast.success(mostra ? "Conversion rate e tempo di risposta inclusi nel PDF" : "Esclusi dal PDF");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="dealer-detail">
      <SectionTitle sub={`Codice ${d.dealer.codice_dealer} · ${d.dealer.settore} · ${d.dealer.provincia || "provincia n/d"} · attivo dal ${dateIt(d.dealer.data_attivazione)}${d.dealer.data_chiusura ? ` · chiuso il ${dateIt(d.dealer.data_chiusura)}` : ""}`}
        right={
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {user?.role !== "VISITATORE" && (
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none
                rounded-md border border-slate-200 bg-white px-3 py-2" title="Vale solo per il PDF esportato"
                data-testid="dealer-pdf-kpi-servizio-label">
                <input type="checkbox" className="h-4 w-4 accent-[#0018A8]" data-testid="dealer-pdf-kpi-servizio"
                  checked={d.dealer.pdf_mostra_kpi_servizio !== false} onChange={togglePdfKpi} />
                Nel PDF: conversion rate e tempo di risposta
              </label>
            )}
            <label className="flex items-center gap-2 text-xs text-slate-600 rounded-md border border-slate-200 bg-white px-3 py-1.5"
              title="Ferma il report a questa data (es. fine del mese precedente). Vuoto = dati a oggi"
              data-testid="dealer-pdf-al-label">
              PDF al
              <input type="date" value={alData} onChange={(e) => setAlData(e.target.value)} max={new Date().toISOString().slice(0, 10)}
                data-testid="dealer-pdf-al" className="text-xs text-slate-800 bg-transparent focus:outline-none" />
              {alData && <button type="button" onClick={() => setAlData("")} data-testid="dealer-pdf-al-reset"
                className="text-slate-400 hover:text-slate-700">×</button>}
            </label>
            <BottoneScarica path={`/report/dealer/${codice}.pdf?anno=${anno}${alData ? `&al=${alData}` : ""}`}
              nomeFallback={`report-dealer-${codice}.pdf`}
              testid="btn-export-dealer-pdf"
              className="px-4 py-2 rounded-md border-2 border-slate-900 text-slate-900 text-sm hover:bg-slate-900 hover:text-white transition-colors duration-200">
              Esporta PDF
            </BottoneScarica>
            <a href={`/api/dealers/${codice}/export-pratiche.xlsx?anno=${anno}&t=${Date.now()}`}
              data-testid="btn-export-dealer-xlsx" title="Pratiche giornaliere dell'anno: venditore, cliente, importo, SIP, servizi, TAN"
              className="px-4 py-2 rounded-md border-2 border-emerald-700 text-emerald-800 text-sm hover:bg-emerald-700 hover:text-white transition-colors duration-200">
              Scarica Excel pratiche
            </a>
            <button type="button" onClick={() => setModifica((v) => !v)} data-testid="btn-modifica-dealer"
              className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-700 transition-colors duration-200">
              {modifica ? "Chiudi" : "Modifica anagrafica"}
            </button>
            <button type="button" onClick={elimina} disabled={eliminando} data-testid="btn-elimina-dealer"
              className="px-4 py-2 rounded-md border border-red-300 text-red-700 text-sm hover:bg-red-50 transition-colors duration-200 disabled:opacity-60">
              Elimina anagrafica
            </button>
            <Link to="/convenzionati" className="text-sm text-slate-500 hover:text-slate-900">← Convenzionati</Link>
          </div>
        }>
        {d.dealer.ragione_sociale}
      </SectionTitle>

      {codice === "NON-IDENTIFICATO" && (
        <ScorporoDealer meta={meta} onDone={() => setReload((r) => r + 1)} />
      )}

      {modifica && (
        <DealerForm meta={meta} dealer={d.dealer} onCancel={() => setModifica(false)}
          onSaved={() => setReload((r) => r + 1)} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <KpiCard testId="d-kpi-caricate" label="Pratiche caricate" value={t.pratiche_caricate} accent="caricato"
          onClick={() => navigate(`/pratiche?${qs({ dealer: codice, anno })}`)} />
        <KpiCard testId="d-kpi-importo" label="Importo caricato" value={t.importo_caricato} kind="eurShort" accent="caricato" />
        <KpiCard testId="d-kpi-liquidate" label="Pratiche liquidate" value={t.pratiche_liquidate || null} accent="liquidato" />
        <KpiCard testId="d-kpi-importo-liq" label="Importo liquidato" value={t.importo_liquidato} kind="eurShort" accent="liquidato" />
        <KpiCard testId="d-kpi-ticket" label="Ticket medio" value={t.ticket_medio} kind="eurShort" />
        <KpiCard testId="d-kpi-conv" label="Conversion rate" value={t.conversion_rate} kind="perc" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Card className="p-6 self-start" data-testid="dealer-anagrafica">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">Anagrafica</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Commerciale</span><span className="font-medium">{d.dealer.commerciale_nome}</span></div>
            {d.dealer.provincia && <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Provincia</span><span className="font-medium">{d.dealer.provincia}</span></div>}
            <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Stato</span><span className="font-medium">{d.dealer.stato}</span></div>
            <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Gestione sorgente</span><span className="font-medium">{d.dealer.gestione_raw}</span></div>
            <div className="flex justify-between py-1"><span className="text-slate-500">Confronto {anno - 1}</span>
              <VarianceIndicator value={d.anno_precedente.pratiche_caricate ? ((t.pratiche_caricate - d.anno_precedente.pratiche_caricate) / d.anno_precedente.pratiche_caricate) * 100 : null} /></div>
          </div>
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mt-6 mb-3">Storico assegnazioni</h3>
          <ul className="space-y-2 text-sm" data-testid="storico-assegnazioni">
            {d.storico_assegnazioni.map((h, i) => (
              <li key={i} className="flex justify-between border-b border-slate-100 py-1">
                <span>{h.commerciale_nome}</span>
                <span className="font-mono text-xs text-slate-500">{dateIt(h.valid_from)} → {h.valid_to ? dateIt(h.valid_to) : "in corso"}</span>
              </li>
            ))}
          </ul>
        </Card>
        <div className="lg:col-span-2 grid grid-cols-1 gap-6">
          <ComparisonChart testId="dealer-chart-pratiche" title={`Pratiche caricate · ${anno - 1} vs ${anno}`} data={serie}
            keys={[{ key: `pratiche_${anno - 1}`, name: `${anno - 1}`, color: COLORS.grigio }, { key: `pratiche_${anno}`, name: `${anno}`, color: COLORS.caricato }]} />
        <ComparisonChart testId="dealer-chart-importo" title={`Importo caricato · ${anno - 1} vs ${anno}`} data={serie}
            keys={[{ key: `caricato_${anno - 1}`, name: `${anno - 1}`, color: COLORS.grigio }, { key: `caricato_${anno}`, name: `${anno}`, color: COLORS.caricato }]}
            emptyNote="importo finanziato non presente nel dataset" />
        <ComparisonChart testId="dealer-chart-liquidate" title={`Pratiche liquidate · ${anno - 1} vs ${anno}`} data={serie}
            keys={[{ key: `liquidate_${anno - 1}`, name: `${anno - 1}`, color: COLORS.grigio }, { key: `liquidate_${anno}`, name: `${anno}`, color: COLORS.liquidato }]} />
        <ComparisonChart testId="dealer-chart-importo-liquidato" title={`Importo liquidato · ${anno - 1} vs ${anno}`} data={serie}
            keys={[{ key: `liquidato_${anno - 1}`, name: `${anno - 1}`, color: COLORS.grigio }, { key: `liquidato_${anno}`, name: `${anno}`, color: COLORS.liquidato }]}
            emptyNote="importo liquidato non presente nel dataset" />
        {d.premi_totale && (
          <ComparisonChart testId="dealer-chart-premi" title={`Premi assicurativi CPI + CVT + NFK + GGN · ${anno - 1} vs ${anno}`}
            data={seriePremi}
            keys={[{ key: `premi_${anno - 1}`, name: `${anno - 1}`, color: COLORS.grigio }, { key: `premi_${anno}`, name: `${anno}`, color: "#0EA5E9" }]} />
        )}
        </div>
      </div>

      <div className="mt-6">
        <SectionTitle sub={`Proiezione a fine ${anno} · progressivo ÷ ${d.calendario.giorni_lavorativi_anno_trascorsi} giorni lavorati × ${d.calendario.giorni_lavorativi_anno} giorni lavorativi`}>
          Proiezioni {anno}
        </SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
          <KpiCard testId="d-proj-pratiche" label="Pratiche caricate" value={p.pratiche_caricate ? Math.round(p.pratiche_caricate) : null} accent="caricatoLight" />
          <KpiCard testId="d-proj-caricato" label={`Importo caricato ${anno}`} value={p.importo_caricato} kind="eurShort" accent="caricato" />
          <KpiCard testId="d-proj-liquidate" label="Pratiche liquidate" value={p.pratiche_liquidate ? Math.round(p.pratiche_liquidate) : null} accent="liquidato" />
          <KpiCard testId="d-proj-liquidato" label={`Importo liquidato ${anno}`} value={p.importo_liquidato} kind="eurShort" accent="liquidato" />
          <KpiCard testId="d-proj-premi" label="Premi assicurativi" value={p.premi} kind="eurShort" accent="sky" />
        </div>
      </div>

      <div className="mt-6">
        <PuntiVenditaScheda codice={codice} punti={d.punti_vendita || []} anno={anno} puoModificare={puo("modifica_configurazioni")}
          onChange={() => setReload((v) => v + 1)} />
      </div>

      <div className="mt-6">
        <RappelProvvigioni codice={codice} anno={anno} rappelIniziale={d.dealer.rappel} puoModificare={puo("modifica_archivio")} />
      </div>

      <div className="mt-10">
        <SectionTitle sub="Ultime 20 pratiche del convenzionato">Pratiche associate</SectionTitle>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dealer-pratiche-table">
            <thead><tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              <th className="text-left px-4 py-3">N° pratica</th><th className="text-left px-4 py-3">Data</th>
              <th className="text-left px-4 py-3">Settore</th><th className="text-left px-4 py-3">Stato</th>
              <th className="text-right px-4 py-3">Importo</th>
            </tr></thead>
            <tbody>
              {pratiche.map((p) => (
                <tr key={p.external_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/pratiche/${p.external_id}`)}>
                  <td className="px-4 py-3 font-mono">{p.numero_pratica}</td>
                  <td className="px-4 py-3 font-mono text-slate-500">{dateIt(p.data_caricamento)}</td>
                  <td className="px-4 py-3">{p.settore}</td>
                  <td className="px-4 py-3"><span className={`text-xs border rounded px-2 py-0.5 ${statoClass(p.stato_norm)}`}>{statoLabel(p.stato_norm)}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{eurShort(p.importo_finanziato)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
